import { ReactNode, useEffect, useState } from "react";

import { useLearnContext } from "../../../context/LearnContext";
import { useTournamentContext } from "../../../context/TournamentContext";
import { VideoContext } from "./VideoContext";

import {
  retryQuiz,
  submitQuiz,
  submitQuizQuestion,
  submitTournamentQuiz,
  submitTournamentQuizQuestion,
  tournamentRetakeQuiz,
} from "sj-ui-toolkit/src/api";
import { Experiment, Variant } from "sj-ui-toolkit/src/api/types/experimentTypes";
import { Events, Resource } from "sj-ui-toolkit/src/api/types/lessonTypes";
import { Assessment, Question, Quiz } from "sj-ui-toolkit/src/api/types/quizTypes";

export interface VideoLessonDetails {
  lessonKey: string;
  id: number;
  type: string;
  title: string;
  description: string;
  isBreakFix: boolean;
  hasExercise: boolean;
  level: string;
  duration: number;
  showBeforeSurvey: boolean;
  showAfterSurvey: boolean;
  videoId: string;
  difficulty?: string;
  resources?: Resource[];
  assessment: Assessment;
  question?: Question;
  questionsData?: Question[];
  events: Events;
  experiment: Experiment;
  steps: number[];
  variant: Variant;
  experimentPassed: boolean;
  lessonPassed: boolean;
}

interface VideoProviderProps {
  children: ReactNode;
  lessonDetails: VideoLessonDetails;
  tournamentUuid?: string;
  onLessonFinish: () => void;
  exerciseOnly?: boolean;
}

// TODO(T2 — TournamentContext conversion): TournamentContext is still JS, so
// `useTournamentContext()` is inferred from its loose `createContext` default
// (`contentId: null`, `handleAttempt: () => void`). Describe the shape this provider
// consumes until T2 types the context, at which point this cast can be removed.
interface ConsumedTournamentContext {
  contentId: number;
  maxPoints: number;
  handleAttempt: (attempt: { maxPoints: number; attemptsCount?: unknown }) => void;
  stopTimeTracking: () => void;
}

export const VideoProvider = ({
  children,
  lessonDetails,
  tournamentUuid,
  onLessonFinish,
  exerciseOnly = false,
}: VideoProviderProps) => {
  const { isTournament } = useLearnContext();
  const { contentId, handleAttempt, maxPoints, stopTimeTracking } =
    useTournamentContext() as unknown as ConsumedTournamentContext;

  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);
  const [inQuizMode, setInQuizMode] = useState(false);
  const [quiz, setQuiz] = useState<Quiz>({
    assessment: lessonDetails.assessment,
    question: lessonDetails.question,
    questionsData: lessonDetails.questionsData,
  });

  const [submissionData, setSubmissionData] = useState<Assessment | null>(null);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<Record<number, string>>({});

  const [inExerciseMode, setInExerciseMode] = useState(exerciseOnly);
  const [isExercisePassed, setIsExercisePassed] = useState(
    lessonDetails?.experiment?.status === "passed" || lessonDetails?.experimentPassed
  );
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [currentQuestionId, setCurrentQuestionId] = useState<number | null>(null);

  const isLessonComplete = exerciseOnly
    ? isExercisePassed
    : quiz.assessment?.result === "passed" && (lessonDetails.hasExercise ? isExercisePassed : true);
  const inLessonMode = !inQuizMode && !inExerciseMode;

  const openFeedbackModal = (questionId: number) => {
    setCurrentQuestionId(questionId);
    setFeedbackModalVisible(true);
  };

  const closeFeedbackModal = () => {
    setFeedbackModalVisible(false);
    setCurrentQuestionId(null);
  };
  const onLessonClick = async () => {
    setInQuizMode(false);
    setInExerciseMode(false);
  };

  const onQuizTake = async () => {
    setInQuizMode(true);
    setInExerciseMode(false);
  };

  const onAnswerSelect = (slug: string) => {
    setSelectedOption(slug);
  };

  const onMultiAnswerSelect = (questionId: number, slug: string) => {
    setSelectedOptions(prev => ({ ...prev, [questionId]: slug }));
  };

  const onQuizSubmit = async () => {
    setQuestionsLoading(true);

    const submissions =
      quiz.questionsData?.map(question => ({
        questionId: question.id,
        answerSlug: selectedOptions[question.id] ?? "",
      })) ?? [];
    const quizSubmission = {
      assessmentId: quiz.assessment.id,
      submissions,
    };

    try {
      let response;
      if (isTournament) {
        response = await submitTournamentQuiz(quizSubmission);
      } else {
        response = await submitQuiz(lessonDetails.lessonKey, quizSubmission);
      }
      const assessmentData = response?.assessmentData;

      if (!assessmentData) {
        throw new Error("No assessment data returned from quiz submission");
      }

      setQuiz({
        ...quiz,
        assessment: {
          ...quiz.assessment,
          answersCount: assessmentData.answersCount,
          assessmentScore: assessmentData.assessmentScore,
          result: assessmentData.result,
        },
        // Post-submission the endpoint returns per-question result rows (feedback)
        // that the review screen reads back from `questionsData`. The field is typed
        // `Question[]` for the answering phase; cast to reuse it for results here.
        questionsData: assessmentData.questionsData as unknown as Question[],
      });

      if (assessmentData.result === "passed") {
        if (isTournament) {
          const newMax = maxPoints - (100 - assessmentData.assessmentScore);
          handleAttempt({
            maxPoints: Math.max(newMax, 10),
          });
        } else if ("nextLesson" in assessmentData) {
          // nextLesson from submission is surfaced via useLessonLoader; nothing to set here
        }
      }
    } catch (error) {
      console.error("Error submitting quiz:", error);
    } finally {
      setQuestionsLoading(false);
    }
  };

  const onQuestionSubmit = async () => {
    if (!selectedOption) {
      console.warn("No option selected for submission");
      return;
    }

    if (!quiz?.question) {
      console.error("No question ID found for submission");
      return;
    }

    try {
      if (isTournament) {
        const data = await submitTournamentQuizQuestion({
          slug: selectedOption,
          questionId: quiz.question.assessmentQuestionId.toString(),
        });
        setSubmissionData(data);
      } else {
        const data = await submitQuizQuestion({
          slug: selectedOption,
          questionId: quiz.question.assessmentQuestionId.toString(),
        });
        setSubmissionData(data);
      }
    } catch (error) {
      console.error("Error submitting quiz question:", error);
    }
  };

  const onQuestionContinue = () => {
    setSelectedOption(null);
    if (submissionData?.nextQuestion) {
      setQuiz({
        ...quiz,
        assessment: {
          ...quiz.assessment,
          answersCount: submissionData.answersCount,
          assessmentScore: submissionData.assessmentScore,
        },
        question: submissionData.nextQuestion,
      });
      setSubmissionData(null);
    }
    if (
      submissionData?.nextQuestion === null &&
      submissionData?.answersCount === quiz.assessment.questionsCount
    ) {
      onQuizFinish();
    }
  };

  const onQuizFinish = () => {
    if (!submissionData) {
      console.warn("No submission data available on quiz finish");
      return;
    }

    setQuiz({
      ...quiz,
      assessment: {
        ...quiz.assessment,
        answersCount: submissionData.answersCount,
        assessmentScore: submissionData.assessmentScore,
        result: submissionData.result,
      },
    });

    if (submissionData.result === "passed") {
      if (!isTournament) {
        // nextLesson is surfaced via useLessonLoader; nothing to set here
      } else {
        const newMax = maxPoints - (100 - submissionData.assessmentScore);
        handleAttempt({
          maxPoints: Math.max(newMax, 10),
        });
      }
    }
  };

  const onQuizRetake = async () => {
    setQuestionsLoading(true);

    try {
      if (isTournament) {
        const data = await tournamentRetakeQuiz({ contentId });
        setQuiz({
          assessment: data.assessment,
          question: data.question,
          questionsData: data.questionsData,
        });
        handleAttempt({
          maxPoints: data.maxPoints,
          attemptsCount: data.attemptsCount,
        });
        setSelectedOptions({});
      } else {
        const data = await retryQuiz(lessonDetails.lessonKey);
        setQuiz(data);
        setSelectedOptions({});
      }
    } catch (error) {
      console.error("Error retaking quiz:", error);
    } finally {
      setQuestionsLoading(false);
    }
    setSubmissionData(null);
  };

  const onQuizReview = () => {
    setInQuizMode(true);
    setInExerciseMode(false);
  };

  const onExerciseLaunch = async () => {
    setInExerciseMode(true);
    setInQuizMode(false);
  };

  const onExercisePassed = () => {
    setIsExercisePassed(true);
  };

  useEffect(() => {
    if (isTournament && isLessonComplete) {
      stopTimeTracking();
    }
  }, [isTournament, isLessonComplete]);

  const ContextValue = {
    // Lesson state
    inLessonMode,
    onLessonClick,
    isLessonComplete,
    onLessonFinish,
    exerciseOnly,

    // Feedback modal state
    feedbackModalVisible,
    openFeedbackModal,
    closeFeedbackModal,

    // Quiz state
    inQuizMode,
    setInQuizMode,
    quiz,
    onQuizTake,
    onQuizFinish,
    onQuizRetake,
    onQuizReview,
    selectedOption,
    onAnswerSelect,
    onQuestionSubmit,
    onQuestionContinue,
    submissionData,
    selectedOptions,
    onMultiAnswerSelect,
    onQuizSubmit,
    questionsLoading,
    currentQuestionId,

    // Exercise state
    inExerciseMode,
    setInExerciseMode,
    onExerciseLaunch,
    isExercisePassed,
    onExercisePassed,

    // Tournament state
    tournamentUuid,
  };

  return <VideoContext.Provider value={ContextValue}>{children}</VideoContext.Provider>;
};
